import { ApiClient } from "client/base";
import { compressFile, isGzip } from "client/customer/meta";
import { detect as detectDelimiter } from "csv-string";
import { detect as detectEncoding } from "chardet";
function toMb(size) {
    return Math.max(Math.round(size / 1024 / 1024), 1);
}
function estimateFileSize(file) {
    const size = toMb(file.size);
    return isGzip(file).then((isGZ) => (isGZ ? size * 15 : size));
}
export function getReadOptions(blob, overrides) {
    if (overrides.type !== undefined && overrides.type !== "CSV") {
        return Promise.resolve(overrides);
    }
    if (overrides.encoding !== undefined && overrides.delimiter !== undefined) {
        return Promise.resolve({
            type: "CSV",
            newlines_in_values: true,
            ...overrides
        });
    }
    return blob
        .slice(0, 5)
        .arrayBuffer()
        .then((headBlob) => {
        const headBytes = new Uint8Array(headBlob);
        if (headBytes[0] == 31 && headBytes[1] == 139) {
            const ds = new window.DecompressionStream("gzip");
            const decompressedStream = blob.stream().pipeThrough(ds);
            const response = new Response(decompressedStream);
            return response.blob().then((b) => getReadOptions(b, {
                ...overrides,
                compression: "GZIP"
            }));
        }
        const head = new TextDecoder().decode(headBytes.slice(0, 5));
        if (head.startsWith("ARROW")) {
            return { type: "ARROW" };
        }
        else if (head.startsWith("PAR1")) {
            return { type: "PARQUET" };
        }
        return blob
            .slice(0, 1024 * 1024)
            .arrayBuffer()
            .then((sample) => {
            const bytes = new Uint8Array(sample);
            let encoding = overrides.encoding ?? detectEncoding(bytes) ?? undefined;
            let delimiter = overrides.delimiter;
            if (encoding !== undefined && delimiter == undefined) {
                let text = "";
                try {
                    text = new TextDecoder(encoding, { fatal: true }).decode(bytes);
                }
                catch {
                    encoding = detectEncoding(bytes) ?? encoding;
                    try {
                        text = new TextDecoder(encoding, { fatal: true }).decode(bytes);
                    }
                    catch {
                        encoding = "UTF-8";
                    }
                }
                if (text.startsWith('{"')) {
                    const jsonOptions = {
                        newlines_in_values: true,
                        ...overrides,
                        type: "JSONL"
                    };
                    if ("encoding" in jsonOptions) {
                        delete jsonOptions.encoding;
                    }
                    return jsonOptions;
                }
                overrides.encoding = encoding;
                delimiter = detectDelimiter(text);
            }
            const options = {
                newlines_in_values: true,
                ...overrides,
                type: "CSV",
                encoding: encoding,
                delimiter: delimiter
            };
            if (overrides.encoding === undefined && encoding !== undefined) {
                // sometimes chardet is wrong so we need to double check
                return blob.arrayBuffer().then((buffer) => {
                    const allBytes = new Uint8Array(buffer);
                    const decoder = new TextDecoder(encoding, { fatal: true });
                    try {
                        decoder.decode(allBytes);
                    }
                    catch {
                        options.encoding = "UTF-8";
                    }
                    return options;
                });
            }
            return options;
        });
    });
}
export class DatasetClient extends ApiClient {
    accountClient;
    constructor(base, accountClient) {
        super(base);
        this.accountClient = accountClient;
    }
    getDataset(id) {
        return this.base.get(`data/${id}`);
    }
    createDataset(payload) {
        return this.base.post("data", payload);
    }
    updateDataset(id, update) {
        return this.base.patch(`data/${id}`, update);
    }
    uploadFile(file, accountId) {
        return this.accountClient.getPresignedUrl(accountId, file.name).then((upload) => {
            return this.accountClient
                .getCustomerClientFactory(accountId)
                .then((ccf) => ccf.getDatasetClient().uploadDataset(file, upload))
                .then(() => {
                return Promise.resolve(upload.data_url);
            });
        });
    }
    uploadDataset(file, folderId, accountId, otherOptions, target, readOptions) {
        return estimateFileSize(file)
            .then((fileSize) => ({
            name: file.name,
            folder_id: folderId,
            account_id: accountId,
            file_size: fileSize
        }))
            .then((payload) => getReadOptions(file, readOptions ?? {}).then((options) => {
            payload.source_content = {
                type: "RAW_DATA",
                options: options
            };
            return payload;
        }))
            .then((payload) => {
            if (target !== undefined) {
                payload.target = target.replace(/(\r\n|\n|\r)/gm, "");
            }
            const options = payload.source_content?.options;
            // if esimated file size is larger it is already compressed
            let skipCompression = true;
            if (options !== undefined && (options.type === undefined || ["CSV", "JSONL"].indexOf(options.type) > -1)) {
                options.compression = "GZIP";
                skipCompression = payload.file_size !== undefined && payload.file_size > toMb(file.size);
            }
            const compression = options?.compression;
            if (compression !== undefined && compression !== "NONE" && compression !== "GZIP" && compression !== "LZ4") {
                throw Error(`unsupported compression ${compression}`);
            }
            const uploadName = skipCompression ? file.name : `${file.name}.gz`;
            return this.accountClient
                .getPresignedUrl(accountId, uploadName)
                .then((upload) => {
                const p = otherOptions.skipUpload
                    ? Promise.resolve()
                    : (skipCompression || compression === undefined || compression === "NONE"
                        ? Promise.resolve(file)
                        : compressFile(file, compression)).then((compressed) => this.accountClient
                        .getCustomerClientFactory(accountId)
                        .then((ccf) => ccf.getDatasetClient().uploadDataset(compressed, upload)));
                return p.then(() => this.createDataset({
                    ...payload,
                    url: upload.data_url.replace(`/${uploadName}`, ""),
                    skip_task_creation: otherOptions.skipUpload
                }));
            })
                .then((response) => {
                if (otherOptions.skipUpload) {
                    return this.createUpload(response.id, otherOptions).then((upload) => Promise.resolve({
                        ...response,
                        metadata: {
                            ...(response.metadata || {}),
                            upload_url: `${window.location.origin}/upload/${upload.id}`
                        }
                    }));
                }
                return Promise.resolve(response);
            });
        });
    }
    linkDataset(credentials, name, folderId, accountId, uploadName = name) {
        const blob = new Blob([JSON.stringify(credentials)], { type: "text/plain" });
        const file = new File([blob], uploadName, { type: blob.type });
        if (credentials.type == "CSV") {
            throw Error("unsuppored type");
        }
        const content = {
            type: "CONNECTION_METADATA",
            provider: credentials.type.toUpperCase()
        };
        if (credentials.table !== undefined) {
            content.table = credentials.table;
        }
        return this.accountClient.getPresignedUrl(accountId, uploadName).then((upload) => this.accountClient
            .getCustomerClientFactory(accountId)
            .then((ccf) => ccf.getDatasetClient().uploadDataset(file, upload))
            .then(() => this.createDataset({
            url: upload.data_url,
            name: name,
            folder_id: folderId,
            account_id: accountId,
            source_content: content
        })));
    }
    createUpload(id, create) {
        const { description, expires_in } = create;
        return this.base.post(`data/${id}/upload`, {
            description,
            expires_in
        });
    }
    patchDataset(id, update) {
        return this.base.patch(`data/${id}`, update);
    }
}
